import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, QrCode, AlertTriangle, CalendarClock, CalendarDays, Clock, CheckCircle2, RotateCcw, FileText, Loader2 } from "lucide-react";
import { papi } from "../../lib/portalApi";
import { brl, formatDate } from "../../lib/api";
import { P, Card, Badge, instTone, SegmentTag } from "./PortalUI";
import { CC } from "./CrisClienteBrand";
import { PixModal } from "./PixModal";

const DATE_TONE = {
  overdue: { label: "Atrasado", fg: P.red, bg: P.redBg, icon: AlertTriangle },
  next: { label: "Próximo vencimento", fg: P.lilacText, bg: P.lilac, icon: CalendarClock },
  future: { label: "Futuro", fg: P.muted, bg: "#F2EFF6", icon: CalendarDays },
};
const CHARGE_TONE = {
  pendente: { label: "PIX gerado · aguardando", fg: P.amber, bg: P.amberBg, icon: Clock },
  em_conciliacao: { label: "Em conciliação", fg: P.lilacText, bg: P.lilac, icon: RotateCcw },
};
const HIST_TONE = {
  confirmado: { label: "Confirmado", fg: P.green, bg: P.greenBg, icon: CheckCircle2 },
  em_conciliacao: { label: "Em conciliação", fg: P.lilacText, bg: P.lilac, icon: RotateCcw },
  em_analise: { label: "Em análise", fg: P.amber, bg: P.amberBg, icon: Clock },
  estornado: { label: "Estornado", fg: P.red, bg: P.redBg, icon: AlertTriangle },
};

export const PaymentCenter = ({ focusDate }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(() => new Set(focusDate ? [focusDate] : []));
  const [pix, setPix] = useState(null);

  const load = useCallback(() => {
    setErr(false);
    papi.get("/portal/payment-center").then((r) => {
      setData(r.data);
      setOpen((cur) => (cur.size ? cur : new Set(r.data.dates.filter((d) => d.state !== "future").slice(0, 1).map((d) => d.due_date))));
    }).catch(() => setErr(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (d) => setOpen((cur) => { const n = new Set(cur); n.has(d) ? n.delete(d) : n.add(d); return n; });

  if (err) return <Card className="p-5 text-sm" data-testid="portal-center-error"><span style={{ color: P.red }}>Não foi possível carregar a Central.</span> <button onClick={load} className="font-semibold underline" style={{ color: P.mid }}>Tentar novamente</button></Card>;
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="animate-spin" style={{ color: P.mid }} /></div>;

  return (
    <div className="space-y-4" data-testid="portal-payment-center">
      <Card className="flex items-center justify-between gap-3 p-4">
        <div>
          <div className="text-[13px]" style={{ color: P.muted }}>Total em aberto</div>
          <div className="text-[24px] font-extrabold tabular-nums" data-testid="portal-center-open-total">{brl(data.open_total)}</div>
        </div>
        <div className="text-right text-[13px]" style={{ color: P.muted }}>{data.dates.length} vencimento{data.dates.length === 1 ? "" : "s"}<br />Escolha a data que quer pagar</div>
      </Card>
      {data.dates.length === 0 && <Card className="p-8 text-center text-[15px]" style={{ color: P.muted }} data-testid="portal-center-empty">Você está em dia. Nenhuma parcela em aberto.</Card>}
      {data.dates.map((d) => <DateCard key={d.due_date} d={d} open={open.has(d.due_date)} onToggle={() => toggle(d.due_date)} onPay={setPix} />)}
      {data.history.length > 0 && <History rows={data.history} />}
      {pix && <PixModal open onClose={() => { setPix(null); load(); }} {...pix} />}
    </div>
  );
};

function DateCard({ d, open, onToggle, onPay }) {
  const tone = DATE_TONE[d.state];
  const multi = d.payment_groups.length > 1;
  return (
    <Card className={`overflow-hidden ${d.state === "overdue" ? "ring-1 ring-[#F4B9C2]" : d.state === "next" ? "ring-1 ring-[#E3C99A]" : ""}`} data-testid={`portal-due-${d.due_date}`}>
      <button onClick={onToggle} aria-expanded={open} className="flex w-full items-center gap-3 p-4 text-left" data-testid={`portal-due-toggle-${d.due_date}`}>
        <span className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl leading-none" style={{ background: tone.bg, color: tone.fg }}>
          <span className="text-[17px] font-extrabold">{d.due_date.slice(8, 10)}</span>
          <span className="text-[10px] font-bold uppercase">{new Date(`${d.due_date}T12:00:00`).toLocaleDateString("pt-BR", { month: "short" }).replace(".", "")}</span>
        </span>
        <span className="min-w-0 flex-1">
          <Badge tone={tone} small>{tone.label}</Badge>
          <span className="mt-1 block text-[20px] font-extrabold tabular-nums" style={{ color: d.state === "overdue" ? P.red : CC.ink }} data-testid={`portal-due-total-${d.due_date}`}>{brl(d.total)}</span>
          <span className="block text-[13px]" style={{ color: P.muted }}>{d.count} parcela{d.count === 1 ? "" : "s"} · {formatDate(d.due_date)}</span>
        </span>
        <ChevronDown size={20} className={`shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} style={{ color: P.muted }} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="space-y-3 border-t px-4 pb-4 pt-3" style={{ borderColor: P.line }}>
            {multi && <div className="rounded-xl px-3 py-2 text-[12px]" style={{ background: P.lilac, color: P.deep }} data-testid="portal-due-split-note">Este vencimento tem cobranças de empresas ou recebedores diferentes. Cada grupo é pago com um PIX próprio.</div>}
            {d.payment_groups.map((g) => <PayGroup key={g.key} d={d} g={g} onPay={onPay} />)}
          </div>
        </div>
      </div>
    </Card>
  );
}

function PayGroup({ d, g, onPay }) {
  const busy = g.charge_state === "em_conciliacao";
  const ct = CHARGE_TONE[g.charge_state];
  return (
    <div className="rounded-xl border p-3" style={{ borderColor: P.line }} data-testid={`portal-paygroup-${d.due_date}-${g.bu}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SegmentTag bu={g.bu} label={g.label} />
        {ct && <Badge tone={ct} small testid={`portal-paygroup-state-${d.due_date}-${g.bu}`}>{ct.label}</Badge>}
      </div>
      <div className="mt-2 divide-y divide-[#ECE4F5]">
        {g.items.map((i) => <ItemRow key={i.installment_id} i={i} disabled={busy} onPay={() => onPay({ bu: i.bu, installmentId: i.installment_id })} />)}
      </div>
      {g.count > 1 && (
        <button disabled={busy} onClick={() => onPay({ group: { due_date: d.due_date, key: g.key } })} data-testid={`portal-pay-group-${d.due_date}-${g.bu}`}
          className="mt-3 flex min-h-12 w-full items-center justify-center gap-2.5 rounded-xl px-3 py-2.5 text-[15px] font-bold text-white shadow-[0_8px_18px_-10px_rgba(61,22,119,0.8)] transition-transform active:scale-[0.99] disabled:opacity-60"
          style={{ background: "linear-gradient(90deg, #4A1E86 0%, #5A2995 100%)" }}>
          <QrCode size={18} /> Pagar as {g.count} parcelas · {brl(g.total)}
        </button>
      )}
    </div>
  );
}

function ItemRow({ i, onPay, disabled }) {
  const tone = instTone(i.status);
  const charged = i.fine > 0 || i.interest > 0;
  return (
    <div className="flex items-center gap-3 py-2.5" data-testid={`portal-center-item-${i.installment_id}`}>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-semibold">Compra #{i.sale_number} · Parcela {i.number}</div>
        <div className="truncate text-[12px]" style={{ color: P.muted }}>{i.reseller_name ? `Revendedora: ${i.reseller_name}` : i.segment}</div>
        <div className="mt-0.5 flex items-center gap-2">
          <span className="text-[14px] font-extrabold tabular-nums">{brl(i.updated_total)}</span>
          {charged && <span className="text-[11px]" style={{ color: P.red }}>inclui multa e juros</span>}
          {i.pix_pending && <span className="text-[11px]" style={{ color: P.amber }}>PIX gerado</span>}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <Badge tone={tone} small>{tone.label}</Badge>
        <button disabled={disabled} onClick={onPay} data-testid={`portal-center-pay-${i.installment_id}`}
          className="rounded-lg border px-3 py-1 text-[12px] font-bold transition-colors hover:bg-[#F3ECFB] disabled:opacity-50" style={{ borderColor: P.mid, color: P.mid }}>
          Pagar
        </button>
      </div>
    </div>
  );
}

function History({ rows }) {
  return (
    <Card className="p-4" data-testid="portal-center-history">
      <div className="flex items-center justify-between">
        <div className="text-[16px] font-bold">Pagamentos agrupados</div>
        <Link to="/cliente/recibos" className="flex items-center gap-1 text-[13px] font-bold" style={{ color: P.mid }} data-testid="portal-center-receipts"><FileText size={15} /> Recibos</Link>
      </div>
      <div className="mt-2 divide-y divide-[#ECE4F5]">
        {rows.map((h) => {
          const t = HIST_TONE[h.state] || HIST_TONE.em_conciliacao;
          return (
            <div key={h.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`portal-center-hist-${h.id}`}>
              <div className="min-w-0">
                <div className="text-[14px] font-extrabold tabular-nums">{brl(h.amount)}</div>
                <div className="text-[12px]" style={{ color: P.muted }}>{h.count} parcelas · venc. {formatDate(h.due_date)}{h.paid_at ? ` · pago em ${formatDate(h.paid_at)}` : ""}</div>
              </div>
              <Badge tone={t} small>{t.label}</Badge>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
